"use client";

import React from "react";
import styled, { css } from "styled-components";
import {
  useFilterState,
  getPageFilterKeys,
} from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import { usePathname } from "next/navigation";
import Chip from "@/components/UI/Chip/Chip";
import mediaQueries from "@/styles/mediaQueries";

const ChipsContainer = styled.div<{ $hasBorder: boolean, $itemsCentered: boolean }>`
  display: none;
  gap: 8px;
  margin: 16px 0;
  overflow-x: auto;
  width: 100%;
  &:not(:empty) {
    display: flex;
  }
  ${mediaQueries.screenSmMin`flex-wrap: wrap; padding-bottom: 0;`};
  ${({ $hasBorder, theme }) =>
    $hasBorder &&
    css`
      display: flex;
      border-bottom: 2px solid
        ${theme.kallisto.color.border.interactive.borderNeutralDefault};
      &:not(:empty) {
        padding-bottom: 1rem;
        ${mediaQueries.screenSmMin`padding-bottom: 1rem;`};
      }
    `}
  ${({ $itemsCentered }) =>
    $itemsCentered &&
    css`
      ${mediaQueries.screenSmMin`justify-content: center;`};
    `}
`;
interface FilterChipsProps {
  hasBorder?: boolean;
  itemsCentered?: boolean;
}

const FilterChips: React.FC<FilterChipsProps> = ({ hasBorder = false, itemsCentered = false }) => {
  const { getActiveFilters, removeFilter, removeFilters, removeAllFilters } =
    useFilterState();
  const pathname = usePathname();

  const allActiveFilters = getActiveFilters();

  // Only show filters that are valid for the current page
  const validFilterKeys = pathname ? getPageFilterKeys(pathname) : [];
  const activeFilters = allActiveFilters.filter((f) =>
    validFilterKeys.includes(f.key)
  );

  // Group tour operator filters
  const tourOperatorFilters = activeFilters.filter(
    (f) => f.key === "tourOperator"
  );

  // Group airline filters
  const airlineFilters = activeFilters.filter((f) => f.key === "airline");

  // Group departure flight time filters (Hinreise)
  const departureFlightFilters = activeFilters.filter(
    (f) =>
      f.key === "departureFlightTimeFrom" || f.key === "departureFlightTimeUntil"
  );

  // Group return flight time filters (Rückreise)
  const returnFlightFilters = activeFilters.filter(
    (f) =>
      f.key === "returnFlightTimeFrom" || f.key === "returnFlightTimeUntil"
  );

  const groupedFilterKeys = [
    "tourOperator",
    "airline",
    "departureFlightTimeFrom",
    "departureFlightTimeUntil",
    "returnFlightTimeFrom",
    "returnFlightTimeUntil",
  ];

  const otherFilters = activeFilters.filter(
    (f) => !groupedFilterKeys.includes(f.key)
  );

  return (
    <ChipsContainer $hasBorder={hasBorder} $itemsCentered={itemsCentered}>
      {activeFilters.length > 0 && (
        <Chip
          key="remove-all"
          onClose={removeAllFilters}
          label="Alle zurücksetzen"
        />
      )}
      {tourOperatorFilters.length > 0 && (
        <Chip
          key="tourOperator-group"
          onClose={() => removeFilters(tourOperatorFilters)}
          label={`${tourOperatorFilters.length} Veranstalter`}
        />
      )}
      {airlineFilters.length > 0 && (
        <Chip
          key="airline-group"
          onClose={() => removeFilters(airlineFilters)}
          label={`${airlineFilters.length} Airline${airlineFilters.length > 1 ? "s" : ""}`}
        />
      )}
      {departureFlightFilters.length > 0 && (
        <Chip
          key="departure-flight-group"
          onClose={() => removeFilters(departureFlightFilters)}
          label="Abflugzeit Hinreise"
        />
      )}
      {returnFlightFilters.length > 0 && (
        <Chip
          key="return-flight-group"
          onClose={() => removeFilters(returnFlightFilters)}
          label="Abflugzeit Rückreise"
        />
      )}
      {otherFilters.map(({ key, value, label }) => (
        <Chip
          key={`${key}-${value}`}
          onClose={() => removeFilter(key, value)}
          label={label}
        />
      ))}
    </ChipsContainer>
  );
};

export default FilterChips;
